import { useState, useEffect } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { Icon } from '../components/Layout'
import { useAuth } from '../context/AuthContext'
import { useToast } from '../context/ToastContext'
import { getCurrentUser } from '../services/auth'
import { getProfile } from '../services/profiles'
import { getMyLeaderboardStats } from '../services/leaderboard'
import { getUserPosts, getUserReshares, likePost, unlikePost, updatePost, deletePost } from '../services/posts'
import { humanizeApiError } from '../services/client'
import PostCard from '../components/PostCard'
import VerifiedBadge from '../components/VerifiedBadge'
import ReferralRewardsModal from '../components/ReferralRewardsModal'
import CisCptExplainer from '../components/CisCptExplainer'
import CptConversionPanel from '../components/CptConversionPanel'
import { CIS_LEVELS, CIS_MAX, formatMultiplier } from '../utils/cisLevels'

const INFLUENCE_MAX = CIS_MAX
const TIER_THRESHOLDS = CIS_LEVELS.map((l) => ({ label: l.label, min: l.min, max: l.max, multiplier: l.multiplier }))

function normalizeTwitter(value) {
  if (!value) return null
  const trimmed = value.trim()
  if (!trimmed) return null

  if (/^https?:\/\//i.test(trimmed)) {
    const handle = trimmed.replace(/^https?:\/\/(www\.)?(twitter|x)\.com\//i, '').replace(/\/+$/, '')
    return { href: trimmed, label: handle ? `@${handle.replace(/^@/, '')}` : trimmed }
  }

  const handle = trimmed.replace(/^@/, '')
  return { href: `https://x.com/${handle}`, label: `@${handle}` }
}

function PostSkeleton() {
  return (
    <div className="border border-on-background/10 lg:neo-border bg-background p-3 lg:p-4 animate-pulse">
      <div className="flex justify-between items-center mb-2">
        <div className="h-2.5 bg-on-background/10 w-28" />
        <div className="h-2 bg-on-background/10 w-16" />
      </div>
      <div className="flex flex-col gap-1.5 mb-3">
        <div className="h-3 bg-on-background/10 w-full" />
        <div className="h-3 bg-on-background/10 w-5/6" />
        <div className="h-3 bg-on-background/10 w-4/6" />
      </div>
    </div>
  )
}

export default function Profile() {
  const [user, setUser] = useState(null)
  const [myStats, setMyStats] = useState(null)
  const [userPosts, setUserPosts] = useState([])
  const [resharedPosts, setResharedPosts] = useState([])
  const [postsTab, setPostsTab] = useState('posts') // 'posts' | 'reshares'
  const [copied, setCopied] = useState(false)
  const [showRewardsInfo, setShowRewardsInfo] = useState(false)
  const [postsLoading, setPostsLoading] = useState(true)
  const { user: authUser, logout } = useAuth()
  const toast = useToast()
  const navigate = useNavigate()

  const [profileError, setProfileError] = useState(null)

  useEffect(() => {
    setProfileError(null)
    getCurrentUser()
      .then(async (data) => {
        const base = data?.data?.user ?? data?.user ?? (data?.id ? data : {})

        try {
          const p = await getProfile()
          const rawCis    = p.userStat?.cisScore?.totalScore ?? p.cisScore   ?? 0
          const rawStreak = p.userStat?.streak?.currentStreak ?? p.streak     ?? 0
          const rawPosts  = p.userStat?.postCount ?? p.postCount  ?? 0
          setUser({
            ...base,
            profileId: p.id,
            name: p.fullName  || base.name || '',
            handle: p.username  ? `@${p.username}` : (base.handle || ''),
            avatar: p.avatarUrl || base.avatar  || '',
            badge: p.verifiedBadge ?? null,
            bio: p.bio || base.bio || '',
            website: p.socialLinks?.website || base.website || '',
            twitter: p.socialLinks?.twitter || base.twitter || '',
            followers: p.followersCount ?? p.followers ?? 0,
            following: p.followingCount ?? p.following ?? 0,
            cltHeld: p.cltHeld ?? 0,
            cisScore: rawCis,
            streak: rawStreak,
            postCount: rawPosts,
            cptEarned: p.cptEarned ?? 0,
            // Fallback to extracting the code from referralLink while both
            // fields are live during the backend transition; drop the
            // fallback once referralLink is fully retired server-side.
            referralCode: p.referralCode ?? (p.referralLink ? p.referralLink.split('/').pop() : null),
          })
          // p.id is the profile-row UUID — posts store this as author.id
          // authUser.id is the user UUID — they are different, so we must use p.id
          setPostsLoading(true)
          Promise.all([
            getUserPosts(p.id ?? base.id),
            getUserReshares(p.id ?? base.id),
          ]).then(([posts, reshares]) => {
            setUserPosts(Array.isArray(posts) ? posts : (posts.data ?? posts.posts ?? []))
            setResharedPosts(Array.isArray(reshares) ? reshares : [])
          }).catch(() => toast.error('Could not load your posts'))
            .finally(() => setPostsLoading(false))
        } catch {
          // profile not found yet — just use auth/me data as-is
          setUser(base)
          setPostsLoading(true)
          getUserPosts(base.id)
            .then(posts => setUserPosts(Array.isArray(posts) ? posts : (posts.data ?? posts.posts ?? [])))
            .catch(() => toast.error('Could not load your posts'))
            .finally(() => setPostsLoading(false))
        }
      })
      .catch(err => { setProfileError(err.message); setUser(authUser); setPostsLoading(false) })
    getMyLeaderboardStats().then(setMyStats).catch(() => {})
    // CPT is in the profile at userStat.cisScore.totalCptEarned (no /wallet endpoint).
    // normalizeProfile (profiles.js) surfaces it as profile.cptEarned — use that.
  }, [authUser])

  const influenceScore = user?.cisScore ?? myStats?.influence?.score ?? 0
  const twitterLink = normalizeTwitter(user?.twitter)
  const currentTier = [...TIER_THRESHOLDS].reverse().find((t) => influenceScore >= t.min) || TIER_THRESHOLDS[0]
  const nextTier = TIER_THRESHOLDS[TIER_THRESHOLDS.indexOf(currentTier) + 1]
  const ptsToNext = nextTier ? Math.max(0, nextTier.min - influenceScore) : 0
  // Progress within the current tier range (Bronze 0-10k, Silver 10k-30k, etc.)
  const tierMin = currentTier.min
  const tierMax = nextTier ? nextTier.min : INFLUENCE_MAX
  const influencePct = nextTier
    ? Math.min(((influenceScore - tierMin) / (tierMax - tierMin)) * 100, 100)
    : 100 // Diamond = full bar

  // Backend now returns the bare referralCode (8-char hex) — the frontend
  // owns building the actual shareable link, since it already owns the
  // /login/:code route shape (see App.jsx ReferralRedirect).
  const referralCode = user?.referralCode ?? ''
  const referralLink = referralCode
    ? `${window.location.origin}/login/${referralCode}`
    : ''

  if (!user) {
    return (
      <div className="flex-1 lg:ml-[300px] w-full max-w-3xl flex items-center justify-center py-20">
        {profileError ? (
          <div className="flex flex-col items-center gap-3 text-on-surface-variant">
            <span className="material-symbols-outlined text-[40px] opacity-30">wifi_off</span>
            <p className="font-bold text-sm uppercase tracking-widest opacity-50">Failed to load profile</p>
          </div>
        ) : (
          <div className="w-8 h-8 border-[3px] border-on-background border-t-transparent animate-spin" />
        )}
      </div>
    )
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(referralLink).catch(() => {})
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const handleEditPost = async (post) => {
    const next = window.prompt('Edit post:', post.content ?? '')
    if (next === null || next.trim() === (post.content ?? '').trim()) return
    const prev = userPosts
    setUserPosts(p => p.map(x => x.id === post.id ? { ...x, content: next.trim() } : x))
    try {
      await updatePost(post.id, { content: next.trim(), categoryId: post.categoryId, isPublished: true })
    } catch (err) {
      setUserPosts(prev)
      toast.error(humanizeApiError(err))
    }
  }

  const handleDeletePost = async (post) => {
    const prevUserPosts = userPosts
    const prevReshared = resharedPosts
    setUserPosts(p => p.filter(x => x.id !== post.id))
    setResharedPosts(p => p.filter(x => x.id !== post.id))
    try {
      await deletePost(post.id)
    } catch {
      setUserPosts(prevUserPosts)
      setResharedPosts(prevReshared)
      toast.error('Could not delete post.')
    }
  }

  // Optimistically reflect a CPT -> CIS conversion in the numbers already
  // shown in the stats row above. A full reconcile happens naturally the
  // next time the profile is fetched (e.g. next mount / navigation back).
  const handleCptConversion = ({ cptSpent, cisCredited }) => {
    setUser((u) => ({
      ...u,
      cptEarned: Math.max(0, (u.cptEarned ?? 0) - cptSpent),
      cisScore: (u.cisScore ?? 0) + cisCredited,
    }))
  }

  return (
    <div className="flex-1 lg:ml-[300px] lg:mr-[340px] w-full max-w-3xl flex flex-col gap-4 lg:gap-8">

      {/* Cover + Avatar */}
      <section>
        <div className="w-full h-28 lg:h-40 bg-primary-container border border-on-background/10 lg:neo-border lg:neo-shadow" />
        <div className="bg-surface-container border border-on-background/10 border-t-0 lg:neo-border lg:border-t-0 p-3 lg:p-6">
          <div className="flex justify-between items-start mb-3 lg:mb-4">
            <div className="-mt-10 lg:-mt-16 w-16 h-16 lg:w-24 lg:h-24 border-2 border-on-background/20 lg:neo-border overflow-hidden bg-surface-container flex items-center justify-center">
              {user.avatar
                ? <img src={user.avatar} alt={user.name} className="w-full h-full object-cover" onError={(e) => { e.target.style.display='none'; e.target.nextSibling.style.display='flex' }} />
                : null}
              <div style={{ display: user.avatar ? 'none' : 'flex' }} className="w-full h-full items-center justify-center">
                <Icon name="person" className="text-[32px] lg:text-[48px] text-on-surface-variant/40" />
              </div>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <NavLink
                to="/profile/edit"
                className="flex items-center gap-1 lg:gap-2 px-3 lg:px-4 py-1.5 lg:py-2 border border-on-background/15 lg:neo-border bg-background font-bold text-[11px] lg:text-[12px] uppercase hover:bg-primary-container/10 transition-colors"
              >
                <Icon name="edit" className="text-[14px] lg:text-[16px]" />
                Edit Profile
              </NavLink>
              <button
                onClick={handleLogout}
                className="flex items-center gap-1 lg:gap-2 px-3 lg:px-4 py-1.5 lg:py-2 border border-on-background/15 lg:neo-border bg-background font-bold text-[11px] lg:text-[12px] uppercase hover:bg-error/10 hover:border-error hover:text-error transition-colors"
              >
                <Icon name="logout" className="text-[14px] lg:text-[16px]" />
                <span className="hidden sm:inline">Log Out</span>
              </button>
            </div>
          </div>

          <h1 className="font-extrabold uppercase tracking-tight leading-none text-[20px] lg:text-[28px] mb-1 flex items-center gap-2">
            {user.name}
            <VerifiedBadge badge={user.badge} size={18} />
          </h1>
          <p className="text-[11px] lg:text-[12px] font-mono text-on-surface-variant mb-2 lg:mb-3">{user.handle}</p>
          <p className="text-sm lg:text-body-md text-on-surface-variant max-w-xl mb-3 lg:mb-4">{user.bio}</p>

          {user.website && (
            <div className="flex items-center gap-2 text-[11px] lg:text-[12px] font-mono text-primary-container mb-3 lg:mb-4">
              <Icon name="link" className="text-[14px] lg:text-[16px]" />
              <span>{user.website}</span>
            </div>
          )}

          {twitterLink && (
            <div className="flex items-center gap-2 text-[11px] lg:text-[12px] font-mono text-primary-container mb-3 lg:mb-4">
              <Icon name="alternate_email" className="text-[14px] lg:text-[16px]" />
              <a
                href={twitterLink.href}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline"
              >
                {twitterLink.label}
              </a>
            </div>
          )}

          {/* Stats row */}
          <div className="mt-3 lg:mt-4 pt-3 lg:pt-4 border-t border-on-background/10">
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 w-full">
              {[
                { label: 'Posts',     value: (user.postCount ?? userPosts.length).toLocaleString() },
                { label: 'Followers', value: (user.followers ?? 0) >= 1000 ? `${((user.followers ?? 0)/1000).toFixed(1)}k` : (user.followers ?? 0) },
                { label: 'Following', value: user.following ?? 0 },
                { label: 'CIS',       value: Number(user.cisScore ?? 0).toLocaleString(), accent: true },
                { label: 'Streak',    value: `${user.streak ?? 0}d` },
                { label: 'CPT',       value: Number(user?.cptEarned ?? 0).toLocaleString(), accent: true },
                { label: 'CLT',       value: Number(user.cltHeld ?? 0).toLocaleString() },
              ].map((stat) => (
                <div key={stat.label} className="flex flex-col items-center border border-on-background/10 bg-background/60 px-2 py-2 lg:py-3">
                  <span className="text-[9px] lg:text-[10px] uppercase text-on-surface-variant font-bold tracking-wide">{stat.label}</span>
                  <span className={`text-sm lg:text-base font-black mt-0.5 ${stat.accent ? 'text-primary-container' : ''}`}>
                    {stat.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Influence Score */}
      <section className="bg-surface-container border border-on-background/10 lg:neo-border lg:neo-shadow p-3 lg:p-6">
        <div className="flex items-center justify-between mb-3 lg:mb-4">
          <h2 className="font-headline-md text-sm lg:text-headline-md uppercase font-black flex items-center gap-2">
            <Icon name="bolt" filled className="text-primary-container text-[18px] lg:text-[22px]" />
            Influence Score
          </h2>
          <div className="flex items-baseline gap-1">
            <span className="text-[22px] lg:text-[32px] font-extrabold text-primary-container leading-none">
              {influenceScore.toLocaleString()}
            </span>
            <span className="text-[10px] lg:text-[12px] text-on-surface-variant font-mono uppercase">
              / {INFLUENCE_MAX >= 1_000_000 ? `${(INFLUENCE_MAX / 1_000_000).toFixed(0)}M` : `${(INFLUENCE_MAX / 1000).toFixed(0)}k`}
            </span>
          </div>
        </div>

        {/* Progress bar */}
        <div className="relative h-3 lg:h-4 bg-background border border-on-background/15 lg:neo-border overflow-hidden mb-2">
          <div
            className="h-full bg-primary-container transition-all duration-700"
            style={{ width: `${influencePct}%` }}
          />
          {/* Tier tick marks */}
          {TIER_THRESHOLDS.slice(1).map((t) => (
            <div
              key={t.label}
              className="absolute top-0 h-full w-px bg-on-background/20"
              style={{ left: `${(t.min / INFLUENCE_MAX) * 100}%` }}
            />
          ))}
        </div>

        {/* Tier labels */}
        <div className="flex justify-between text-[9px] lg:text-[10px] uppercase font-bold text-on-surface-variant mb-3 lg:mb-4">
          {TIER_THRESHOLDS.map((t) => (
            <span
              key={t.label}
              className={t.label === currentTier.label ? 'text-primary-container' : ''}
            >
              {t.label}
            </span>
          ))}
        </div>

        {/* Current tier + next milestone */}
        <div className="flex items-center justify-between bg-background border border-on-background/10 lg:neo-border p-2 lg:p-3">
          <div className="flex items-center gap-2">
            <span className="text-[10px] lg:text-[12px] uppercase font-bold text-on-surface-variant">Current Tier</span>
            <span className="px-2 py-0.5 bg-primary-container text-on-primary-fixed text-[10px] lg:text-[11px] font-bold uppercase tracking-wider">
              {currentTier.label}
            </span>
            <span className="text-[10px] lg:text-[11px] font-bold text-on-surface-variant">
              {formatMultiplier(currentTier.multiplier)} rewards
            </span>
          </div>
          {nextTier && (
            <div className="text-right">
              <span className="text-[10px] lg:text-[11px] text-on-surface-variant font-mono">
                +{ptsToNext.toLocaleString(undefined, { maximumFractionDigits: 0 })} pts → <strong>{nextTier.label}</strong>
              </span>
            </div>
          )}
        </div>

        <NavLink
          to="/levels"
          className="inline-block mt-2 text-[10px] lg:text-[11px] font-bold uppercase tracking-widest text-primary-container hover:underline"
        >
          See level requirements &amp; how CIS → CPT works →
        </NavLink>
      </section>

      {/* CIS → CPT explainer */}
      <CisCptExplainer />

      {/* Season Rewards — convert CIS into CPT during an active reward season */}
      <CptConversionPanel cptBalance={user.cptEarned ?? 0} onConverted={handleCptConversion} />

      {/* Referral Link */}
      <section className="bg-surface-container border border-on-background/10 lg:neo-border lg:neo-shadow p-3 lg:p-6">
        <div className="flex items-center justify-between mb-1 lg:mb-2">
          <h2 className="font-headline-md text-sm lg:text-headline-md uppercase font-black flex items-center gap-2">
            <Icon name="group_add" className="text-primary-container text-[18px] lg:text-[22px]" />
            Referral Link
          </h2>
          <button
            onClick={() => setShowRewardsInfo(true)}
            title="How referral rewards work"
            className="flex items-center gap-1 text-[10px] lg:text-[11px] font-bold uppercase text-primary-container hover:underline"
          >
            <Icon name="info" className="text-[16px] lg:text-[18px]" />
            <span className="hidden sm:inline">How it works</span>
          </button>
        </div>
        <p className="text-[11px] lg:text-[12px] text-on-surface-variant mb-3 lg:mb-4">
          Invite friends to Chicago — you both earn <span className="text-primary-container font-bold">CIS</span> as they get active.
        </p>

        {/* Link box */}
        <div className="flex items-stretch gap-0 border border-on-background/15 lg:neo-border overflow-hidden">
          <div className="flex-1 bg-background px-3 py-2 lg:py-3 font-mono text-[11px] lg:text-[13px] text-on-surface-variant truncate select-all">
            {referralLink}
          </div>
          <button
            onClick={handleCopy}
            className={`shrink-0 flex items-center gap-1.5 px-3 lg:px-4 py-2 lg:py-3 font-bold text-[11px] lg:text-[12px] uppercase tracking-wider transition-all
              ${copied
                ? 'bg-green-600 text-white'
                : 'bg-primary-container text-on-primary-fixed hover:brightness-105 active:scale-95'
              }`}
          >
            <Icon name={copied ? 'check' : 'content_copy'} className="text-[14px] lg:text-[16px]" />
            <span className="hidden sm:inline">{copied ? 'Copied!' : 'Copy'}</span>
          </button>
        </div>

        {/* Referral code */}
        <div className="flex items-center gap-2 mt-2 lg:mt-3">
          <span className="text-[10px] lg:text-[11px] text-on-surface-variant uppercase font-bold">Code:</span>
          <span className="font-mono text-[11px] lg:text-[13px] text-primary-container tracking-widest">{referralCode}</span>
        </div>
      </section>

      {/* Posts + Reshares */}
      <section className="bg-surface-container border border-on-background/10 lg:neo-border lg:neo-shadow">
        {/* Tab header */}
        <div className="flex border-b border-on-background/10">
          {[
            { key: 'posts',    label: 'Posts',    count: userPosts.length },
            { key: 'reshares', label: 'Reshares', count: resharedPosts.length },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setPostsTab(tab.key)}
              className={`flex-1 flex items-center justify-center gap-2 py-3 lg:py-4 text-[11px] lg:text-[12px] font-bold uppercase tracking-wider transition-all border-b-2 ${
                postsTab === tab.key
                  ? 'border-primary-container text-primary-container'
                  : 'border-transparent text-on-surface-variant hover:text-on-background'
              }`}
            >
              {tab.label}
              <span className={`px-1.5 py-0.5 text-[9px] font-black ${postsTab === tab.key ? 'bg-primary-container text-on-primary-fixed' : 'bg-on-background/10 text-on-surface-variant'}`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        <div className="p-3 lg:p-6">
        {postsLoading ? (
          <div className="flex flex-col gap-3 lg:gap-4">
            <PostSkeleton />
            <PostSkeleton />
            <PostSkeleton />
          </div>
        ) : postsTab === 'reshares' ? (
          resharedPosts.length === 0 ? (
            <p className="text-on-surface-variant py-6 lg:py-8 text-center text-sm">No reshares yet.</p>
          ) : (
            <div className="flex flex-col gap-3 lg:gap-6">
              {resharedPosts.map((post) => (
                <div key={post.id} className="relative">
                  <div className="flex items-center gap-1.5 mb-1.5 text-[10px] font-bold uppercase tracking-widest text-primary-container">
                    <Icon name="repeat" className="text-[14px]" />
                    <span>Reshared</span>
                  </div>
                  <PostCard key={post.id} post={post} onDelete={handleDeletePost} onLike={async () => {}} />
                </div>
              ))}
            </div>
          )
        ) : userPosts.length === 0 ? (
          <p className="text-on-surface-variant py-6 lg:py-8 text-center text-sm">No posts yet.</p>
        ) : (
          <div className="flex flex-col gap-3 lg:gap-6">
            {userPosts.map((post) => (
              <PostCard
                key={post.id}
                post={post}
                onEdit={handleEditPost}
                onDelete={handleDeletePost}
                onLike={async (postId) => {
                  const p = userPosts.find(x => x.id === postId)
                  if (!p) return
                  setUserPosts(prev => prev.map(x =>
                    x.id === postId
                      ? { ...x, liked: !x.liked, likes: x.liked ? (Number(x.likes)||0)-1 : (Number(x.likes)||0)+1 }
                      : x
                  ))
                  try {
                    if (p.liked) await unlikePost(postId)
                    else await likePost(postId)
                  } catch {
                    setUserPosts(prev => prev.map(x => x.id === postId ? { ...x, liked: p.liked, likes: p.likes } : x))
                  }
                }}
              />
            ))}
          </div>
        )}
        </div>
      </section>

      <div className="h-20 md:h-0" />
      {showRewardsInfo && <ReferralRewardsModal onClose={() => setShowRewardsInfo(false)} />}
    </div>
  )
}
